import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemText,
  Pagination,
} from '@mui/material';
import { useInventory } from '../hooks/useInventory';
import EmptyState from '@shared/ui/common/EmptyState';
import { Paper } from '@shared/ui/common/paper';

import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { InventoryStatus } from './InventoryStatus';
import { Product } from '@domain/product/models/Product';
import { useGenericDrawer } from '@shared/hooks';
import ProductDetailsDrawer from '@features/products/components/detail/ProductDetailsDrawer';
import { Icon } from '@shared/icons';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import InventoryForecastGradeNoneIndicator from './InventoryForecastGradeNoneIndicator';



const Inventory = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWarehouses, setSelectedWarehouses] = useState<any[]>([]);

  // Use the products filter hook with storage
  const { filterCriteria, page, setPage, limit } = useProductsFilter(50);

  const { openDrawer } = useGenericDrawer();

  const { inventory, isLoading, isFetching } = useInventory(
    {
      ...filterCriteria,
      page,
      limit,
      purchasable: true,
    }
  );

  const handleChipClick = (event: React.MouseEvent<HTMLElement>, warehouses: any[]) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedWarehouses(warehouses);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedWarehouses([]);
  };

  const handleProductClick = (product: Product) => {
    openDrawer('Product Details', <ProductDetailsDrawer productId={product.id} />);
  }


  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} py={2}>
        <Stack direction="row" alignItems="center" spacing={2} flex={1}>
          <ProductsFilter />
          <ActiveFilters />
        </Stack>

        
        <InventoryForecastGradeNoneIndicator />
      </Stack>

      <>

        {isLoading || isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : inventory?.status === 'error' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {inventory.error.message}
            </Typography>
          </Box>

        ) : inventory?.status === 'success' && inventory?.data?.length === 0 ? (

          <EmptyState
            title="No Inventory"
            description="Make sure you have products that are replenishable to see the inventory levels."
            action={{
              label: 'Make products replenishable',
              onClick: () => {
                navigate('/product-catalog/products');
              }
            }}
          />

        ) : inventory?.status === 'success' && (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 3 }}>Product</TableCell>
                    <TableCell sx={{ width: '200px', px: 3 }}>Inventory level</TableCell>
                    <TableCell sx={{ width: '200px', px: 3 }}>In transit</TableCell>
                    <TableCell sx={{ width: '400px', px: 3 }}>Days of inventory</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.data.map((inventoryItem) => (
                    <TableRow
                      hover
                      onClick={() => handleProductClick(inventoryItem.product)}
                      key={inventoryItem.product.id}
                      sx={{
                        cursor: 'pointer',
                        '& td': {
                          borderBottom: 1,
                          borderColor: 'divider',
                          py: 2,
                        },
                      }}
                    >
                      {/* Product */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={2} p={1}>
                          <Avatar
                            src={inventoryItem.product.image}
                            alt={inventoryItem.product.title}
                            variant="rounded"
                            sx={{
                              width: 40,
                              height: 40,
                              border: 1,
                              borderColor: 'divider',
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flexGrow: 1,
                            }}
                          >
                            {inventoryItem.product.title}
                          </Typography>

                        </Stack>
                      </TableCell>

                      {/* Available inventory */}
                      <TableCell sx={{ px: 3 }}>
                        <Stack direction="column" spacing={1} useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {inventoryItem.warehouses.reduce((sum, w) => sum + w.quantity, 0)} items available
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {inventoryItem.warehouses
                              .sort((a, b) => b.quantity - a.quantity)
                              .slice(0, 2)
                              .map((warehouse) => (
                                <Chip
                                  color="info"
                                  key={warehouse.id}
                                  label={`${warehouse.quantity} in ${warehouse.name}`}
                                  size="small"
                                  sx={{ maxWidth: '200px' }}
                                />
                              ))}
                            {inventoryItem.warehouses.length > 2 && (
                              <Chip
                                color="info"
                                label={`+${inventoryItem.warehouses.length - 2}`}
                                size="small"
                                onClick={(event) => handleChipClick(event, inventoryItem.warehouses.slice(2))}
                                sx={{
                                  maxWidth: '100px',
                                  backgroundColor: 'background.paper',
                                  border: 1,
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                  '& .MuiChip-label': { fontSize: '0.75rem' },
                                }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>

                      {/* In transit */}
                      <TableCell sx={{ px: 3 }}>
                        <Stack direction="column" spacing={1} useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {inventoryItem.purchaseOrderItems?.reduce(
                              (sum, w) => sum + (w.quantity || 0),
                              0
                            )}{' '}
                            items in transit
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {inventoryItem.purchaseOrderItems
                              ?.sort((a, b) => (b.quantity || 0) - (a.quantity || 0))
                              .slice(0, 2)
                              .map((orderItem) => (
                                <Chip
                                  color="info"
                                  key={orderItem.id}
                                  label={
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Icon icon="Calendar01" />
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ fontWeight: 'medium' }}
                                      >
                                        {orderItem.shipment
                                          ? format(orderItem.shipment.processedDate, 'dd MMM')
                                          : orderItem.eta && format(orderItem.eta, 'dd MMM')}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 'medium' }}
                                      >
                                        {orderItem.quantity || 0} items
                                      </Typography>
                                    </Stack>
                                  }
                                  size="small"
                                />
                              ))}
                            {inventoryItem.purchaseOrderItems &&
                              inventoryItem.purchaseOrderItems.length > 2 && (
                                <Chip
                                  label={`+${inventoryItem.purchaseOrderItems.length - 2}`}
                                  size="small"
                                  color="info"
                                  onClick={(event) =>
                                    handleChipClick(
                                      event,
                                      inventoryItem.purchaseOrderItems?.slice(2) || []
                                    )
                                  }
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    '& .MuiChip-label': { fontSize: '0.75rem' },
                                  }}
                                />
                              )}
                          </Stack>
                        </Stack>
                      </TableCell>

                      {/* Inventory status */}
                      <TableCell sx={{ px: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <InventoryStatus inventory={inventoryItem} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack p={2} direction="row" justifyContent="center" alignItems="center">
              <Pagination
                count={inventory?.totalPages || 0}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Stack>
          </>
        )}
      </>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 200,
          }
        }}
      >
        {selectedWarehouses.map((warehouse) => (
          <MenuItem key={warehouse.id} onClick={handleClose}>
            <ListItemText
              primary={`${warehouse.quantity} in ${warehouse.name}`}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontSize: '0.875rem' }
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default Inventory;